import { EyeOutlined } from '@ant-design/icons';
import { getVerifiedCustomers } from '@app/api/customer.api';
import { getPreClearanceTableData } from '@app/api/pre-clearance.api';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { BaseButtonsForm } from '@app/components/common/forms/BaseButtonsForm/BaseButtonsForm';
import FilterComponent from '@app/components/TableFilter/TableFilter';
import { notificationController } from '@app/controllers/notificationController';
import { useMounted } from '@app/hooks/useMounted';
import { Pagination } from '@app/models/Pagination';
import { PreClearanceTableRow } from '@app/models/pre_clearance/PreClearanceResponse';
import { TablePaginationConfig, Space, Tooltip, Button, Tag } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const initialPagination: Pagination = {
  current: 1,
  pageSize: 5,
};

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'default',
  PENDING_DOCUMENT: 'orange',
  PENDING_REVIEW: 'gold',
  PENDING_RECOMMENDATION: 'cyan',
  PENDING_APPROVAL: 'blue',
  PENDING_ADDITIONAL_APPROVAL: 'geekblue',
  PENDING_DIRECTOR_APPROVAL: 'purple',
  PENDING_DG_APPROVAL: 'magenta',
  DONE: 'green',
  REJECTED: 'red',
};

// Fallback labels in case the API doesn't send status_label
const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  PENDING_DOCUMENT: 'Pending Document',
  PENDING_REVIEW: 'Pending Document Review',
  PENDING_RECOMMENDATION: 'Pending Recommendation',
  PENDING_APPROVAL: 'Pending DAPH Approval',
  PENDING_ADDITIONAL_APPROVAL: 'Pending Additional Approval',
  DONE: 'Completed',
  REJECTED: 'Rejected',
  PENDING_DIRECTOR_APPROVAL: 'Pending Director Approval',
  PENDING_DG_APPROVAL: 'Pending DG Approval',
};

const PreClearanceTable = () => {
  const [tableData, setTableData] = useState<{
    data: PreClearanceTableRow[];
    pagination: Pagination;
    loading: boolean;
  }>({
    data: [],
    pagination: initialPagination,
    loading: false,
  });
  const { isMounted } = useMounted();
  const navigate = useNavigate();
  const [form] = BaseButtonsForm.useForm();
  const [filters, setFilters] = useState<{ [key: string]: string | [string, string] | null }>({});
  type FilterOption = { label: string; value: string };

  const [statusOptions, setStatusOptions] = useState<FilterOption[]>([]);
  const [productCategoryOptions, setProductCategoryOptions] = useState<FilterOption[]>([]);
  const selectedPCType = localStorage.getItem('selected_pc_type');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userType = user?.usertype;

  const fetch = useCallback(
    (pagination: Pagination) => {
      setTableData((tableData) => ({ ...tableData, loading: true }));
      getPreClearanceTableData({
        ...pagination,
        ...filters,
        type: selectedPCType || 'IMPORT_PRE_CLEARANCE',
      })
        .then((res) => {
          if (isMounted.current) {
            setTableData({
              data: res.data,
              pagination: {
                current: res.pagination.current,
                pageSize: res.pagination.pageSize,
                total: res.pagination.total,
              },
              loading: false,
            });
            setStatusOptions(
              Object.entries(res.status_options ?? {}).map(([value, label]) => ({ value, label: String(label) })),
            );
            setProductCategoryOptions(
              (res.product_category_options ?? []).map((o: { value: number; label: string }) => ({
                value: String(o.value),
                label: o.label,
              })),
            );
          }
        })
        .catch((error: any) => {
          setTableData({ data: [], pagination: initialPagination, loading: false });
          notificationController.error({
            message: error.message,
          });
        });
    },
    [isMounted, filters, selectedPCType],
  );

  useEffect(() => {
    fetch(initialPagination);
  }, [fetch]);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    fetch({
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 5,
    });
  };

  const handleViewRow = (id: number) => {
    navigate(`/view-pre-clearance`, { state: { id: id } });
  };

  const showTotal = (total: number, range: [number, number]): React.ReactNode => {
    return (
      <span
        style={{ fontSize: '14px', marginRight: '20px', color: '#adadad' }}
      >{`Showing ${range[0]} - ${range[1]} of ${total} Pre-Clearance`}</span>
    );
  };

  const resolveCustomerId = async (email: string): Promise<string | null> => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      notificationController.error({ message: 'Please enter a valid email address.' });
      return null;
    }
    try {
      const data = await getVerifiedCustomers(email);
      if (!data?.id) {
        notificationController.warning({ message: 'No customer found with that email address.' });
        return null;
      }
      return String(data.id);
    } catch (error: any) {
      notificationController.error({ message: error.message || 'Failed to fetch customer data.' });
      return null;
    }
  };

  const searchData: {
    name: string;
    placeholder: string;
    type: 'text' | 'dropdown';
    options?: Record<string, { label: string; value: string }[]>;
    resolve?: (value: string) => Promise<string | null>;
  }[] = [
    { name: 'search', placeholder: 'Search Code/ H.S Code', type: 'text' },
    { name: 'customer_id', placeholder: 'Customer Email', type: 'text', resolve: resolveCustomerId },
    {
      name: 'product_category_id',
      placeholder: 'Product Category',
      type: 'dropdown',
      options: productCategoryOptions ? { productCategoryOptions: productCategoryOptions } : {},
    },
    {
      name: 'status',
      placeholder: 'status',
      type: 'dropdown',
      options: statusOptions ? { statusOptions: statusOptions } : {},
    },
  ];

  const columns: ColumnsType<PreClearanceTableRow> = [
    ...(userType === 'ADMIN'
      ? [
          {
            title: 'Customer',
            dataIndex: 'customer_name',
            key: 'customer_name',
          },
        ]
      : []),
    {
      title: 'Code',
      dataIndex: 'reference_code',
      key: 'reference_code',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string, record) => (
        <Tag color={STATUS_COLORS[status] ?? 'default'}>{record.status_label ?? STATUS_LABELS[status] ?? status}</Tag>
      ),
    },
    {
      title: 'Product Category',
      dataIndex: 'product_category_name',
      key: 'product_category_name',
    },
    {
      title: 'Product',
      dataIndex: 'product_name',
      key: 'product_name',
    },
    {
      title: 'Department',
      dataIndex: 'department_name',
      key: 'department_name',
    },
    {
      title: 'Port of Entry',
      dataIndex: 'location_name',
      key: 'location_name',
    },
    {
      title: 'Performa Invoice No',
      dataIndex: 'invoice_no',
      key: 'invoice_no',
    },
    {
      title: 'H. S. Code',
      dataIndex: 'hs_code',
      key: 'hs_code',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        return (
          <Space>
            <Tooltip title="View">
              <Button
                type="text"
                onClick={() => handleViewRow(record.id)}
                icon={<EyeOutlined />}
                style={{ color: '#1890ff' }}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];
  return (
    <BaseCard id="basic-table" title="Pre-Clearance" padding="1.25rem">
      <FilterComponent data={searchData} onFilter={setFilters} />
      <BaseTable
        columns={columns}
        rowKey="id"
        dataSource={tableData.data}
        pagination={{
          current: tableData.pagination.current,
          pageSize: tableData.pagination.pageSize,
          total: tableData.pagination.total,
          showTotal,
        }}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: true, scrollToFirstRowOnChange: true }}
      />
    </BaseCard>
  );
};

export default PreClearanceTable;
